// Where, which way and when an existing photo was taken, from the EXIF data
// the camera wrote into it (issue #436). A photo chosen from the library wasn't
// taken here and now, so the device's own GPS and compass are wrong for it:
// it gets only what its EXIF says, and anything missing or doubtful is left
// empty — never the device's current reading — so it never pins at the wrong
// spot on a site plan.
//
// Only JPEG is read (iPad and iPhone hand a library photo over as JPEG). A
// position counts only with its accuracy (GPSHPositioningError, which iPad
// and iPhone cameras write): without one it can't be judged against a saved
// point. A time counts only with its UTC offset, or it can't be placed.
//
// The file is untrusted: every length, count and offset is checked to lie
// inside the bytes read before it's used, and anything that doesn't is
// malformed — the photo then has no EXIF (PR #463 review).

import type { Position } from './location';

export interface PhotoExif {
  position: Position | null;
  /** Degrees from north, clockwise. */
  bearing: number | null;
  /** ISO time it was taken. */
  takenAt: string | null;
  /** The camera's make and model ("Apple iPad Pro"), for the photo's row. */
  camera: string | null;
}

const NOTHING: PhotoExif = { position: null, bearing: null, takenAt: null, camera: null };
/** hig_camera's length in CRM. */
const CAMERA_MAX = 200;

/** EXIF sits at the start of a JPEG (APP1 is at most 64 KB), so this much is enough to read. */
export const EXIF_HEAD_BYTES = 128 * 1024;

const JPEG_START = 0xffd8;
const APP1 = 0xffe1;
const START_OF_SCAN = 0xffda;
const EXIF_HEADER = 'Exif\0\0';
const TIFF_LITTLE_ENDIAN = 0x4949; // "II"
const TIFF_MAGIC = 42;

const TAG_MAKE = 0x010f;
const TAG_MODEL = 0x0110;
const TAG_EXIF_IFD = 0x8769;
const TAG_GPS_IFD = 0x8825;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_OFFSET_TIME_ORIGINAL = 0x9011;
const GPS_LATITUDE_REF = 0x01;
const GPS_LATITUDE = 0x02;
const GPS_LONGITUDE_REF = 0x03;
const GPS_LONGITUDE = 0x04;
const GPS_IMG_DIRECTION_REF = 0x10;
const GPS_IMG_DIRECTION = 0x11;
const GPS_H_POSITIONING_ERROR = 0x1f;

/** Bytes per value, by TIFF field type. */
const TYPE_SIZE: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 };
const TYPE_ASCII = 2;
const TYPE_SHORT = 3;
const TYPE_LONG = 4;
const TYPE_RATIONAL = 5;

const FULL_CIRCLE = 360;

const ENTRY_BYTES = 12;
/** A JPEG segment's length counts its own two bytes. */
const SEGMENT_LENGTH_BYTES = 2;

/** Thrown for a length, count or offset that doesn't fit the file; readPhotoExif turns it into no EXIF. */
class Malformed extends Error {}

/** `bytes` bytes from `at` must lie inside the view. */
function within(view: DataView, at: number, bytes: number): void {
  if (!Number.isSafeInteger(at) || !Number.isSafeInteger(bytes) || at < 0 || bytes < 0 || at + bytes > view.byteLength) throw new Malformed();
}

interface Entry {
  type: number;
  count: number;
  /** Where the value starts in the view. */
  at: number;
}

class Tiff {
  private readonly view: DataView;
  private readonly start: number;
  private readonly little: boolean;

  constructor(view: DataView, start: number) {
    this.view = view;
    this.start = start;
    this.little = view.getUint16(start) === TIFF_LITTLE_ENDIAN;
    if (view.getUint16(start + 2, this.little) !== TIFF_MAGIC) throw new Error('Not TIFF');
  }

  firstIfd(): number {
    return this.offset(this.start + 4);
  }

  /** The TIFF offset held at `at`, as a place in the view — which must be inside it. */
  private offset(at: number): number {
    const target = this.start + this.view.getUint32(at, this.little);
    within(this.view, target, 1);
    return target;
  }

  /** The entries of the directory at `at`, by tag. */
  entries(at: number): Map<number, Entry> {
    const found = new Map<number, Entry>();
    const count = this.view.getUint16(at, this.little);
    // The whole directory must be in the file; so must every value it points at.
    within(this.view, at + 2, count * ENTRY_BYTES);
    for (let i = 0; i < count; i++) {
      const entry = at + 2 + i * ENTRY_BYTES;
      const type = this.view.getUint16(entry + 2, this.little);
      const valueCount = this.view.getUint32(entry + 4, this.little);
      const size = (TYPE_SIZE[type] ?? 0) * valueCount;
      // Four bytes or fewer are held in the entry itself; more, at an offset.
      const valueAt = size <= 4 ? entry + 8 : this.offset(entry + 8);
      within(this.view, valueAt, size);
      found.set(this.view.getUint16(entry, this.little), { type, count: valueCount, at: valueAt });
    }
    return found;
  }

  pointer(entry: Entry | undefined): number | null {
    if (!entry || entry.type !== TYPE_LONG) return null;
    return this.offset(entry.at);
  }

  text(entry: Entry | undefined): string | null {
    if (!entry || entry.type !== TYPE_ASCII) return null;
    let out = '';
    for (let i = 0; i < entry.count; i++) {
      const code = this.view.getUint8(entry.at + i);
      if (code === 0) break;
      out += String.fromCharCode(code);
    }
    return out;
  }

  rationals(entry: Entry | undefined): number[] | null {
    if (!entry || entry.type !== TYPE_RATIONAL) return null;
    const values: number[] = [];
    for (let i = 0; i < entry.count; i++) {
      const at = entry.at + i * 8;
      values.push(this.view.getUint32(at, this.little) / this.view.getUint32(at + 4, this.little));
    }
    return values.every(Number.isFinite) ? values : null;
  }

  short(entry: Entry | undefined): number | null {
    return entry?.type === TYPE_SHORT ? this.view.getUint16(entry.at, this.little) : null;
  }
}

/** Where the TIFF block inside a JPEG's EXIF segment starts, or null if it has none. */
function findTiff(view: DataView): number | null {
  if (view.getUint16(0) !== JPEG_START) return null;
  let at = 2;
  while (at + 4 <= view.byteLength) {
    const marker = view.getUint16(at);
    if (marker === START_OF_SCAN || (marker & 0xff00) !== 0xff00) return null;
    const length = view.getUint16(at + 2);
    // Too short to hold its own length, or running past the bytes read: not a real segment.
    if (length < SEGMENT_LENGTH_BYTES) throw new Malformed();
    within(view, at + 2, length);
    if (marker === APP1 && length >= SEGMENT_LENGTH_BYTES + EXIF_HEADER.length) {
      let header = '';
      for (let i = 0; i < EXIF_HEADER.length; i++) header += String.fromCharCode(view.getUint8(at + 4 + i));
      if (header === EXIF_HEADER) return at + 4 + EXIF_HEADER.length;
    }
    at += 2 + length;
  }
  return null;
}

/** One axis's hemisphere letters and its largest value in degrees. */
interface Axis {
  positive: string;
  negative: string;
  limit: number;
}

const MINUTES_PER_DEGREE = 60;
const LATITUDE: Axis = { positive: 'N', negative: 'S', limit: 90 };
const LONGITUDE: Axis = { positive: 'E', negative: 'W', limit: 180 };
/** The direction is from true or magnetic north; any other reference isn't trusted. */
const DIRECTION_REFS = ['T', 'M'];

/**
 * Degrees, minutes, seconds and the axis's own hemisphere letter to signed
 * degrees. Any other reference (missing, lowercase, the wrong axis's) = null:
 * the file is untrusted, and a guessed hemisphere would pin the wrong spot.
 */
function toDegrees(parts: number[] | null, ref: string | null, axis: Axis): number | null {
  if (!parts || parts.length !== 3 || (ref !== axis.positive && ref !== axis.negative)) return null;
  // Minutes and seconds past 60 aren't a real reading.
  if (parts[1] >= MINUTES_PER_DEGREE || parts[2] >= MINUTES_PER_DEGREE) return null;
  const degrees = parts[0] + parts[1] / MINUTES_PER_DEGREE + parts[2] / (MINUTES_PER_DEGREE * MINUTES_PER_DEGREE);
  if (degrees > axis.limit) return null;
  return ref === axis.negative ? -degrees : degrees;
}

function readPosition(tiff: Tiff, gps: Map<number, Entry>): Position | null {
  const latitude = toDegrees(tiff.rationals(gps.get(GPS_LATITUDE)), tiff.text(gps.get(GPS_LATITUDE_REF)), LATITUDE);
  const longitude = toDegrees(tiff.rationals(gps.get(GPS_LONGITUDE)), tiff.text(gps.get(GPS_LONGITUDE_REF)), LONGITUDE);
  const accuracy = tiff.rationals(gps.get(GPS_H_POSITIONING_ERROR))?.[0];
  if (latitude === null || longitude === null || accuracy === undefined || accuracy < 0) return null;
  // 0,0 is what some apps write for "no fix".
  if (latitude === 0 && longitude === 0) return null;
  return { latitude, longitude, accuracy };
}

function readBearing(tiff: Tiff, gps: Map<number, Entry>): number | null {
  if (!DIRECTION_REFS.includes(tiff.text(gps.get(GPS_IMG_DIRECTION_REF)) ?? '')) return null;
  const bearing = tiff.rationals(gps.get(GPS_IMG_DIRECTION))?.[0];
  if (bearing === undefined || bearing < 0 || bearing > FULL_CIRCLE) return null;
  return Math.round(bearing) % FULL_CIRCLE;
}

/** Make and model as one name; a model that already starts with the make ("Canon EOS R6") isn't doubled. */
function readCamera(tiff: Tiff, top: Map<number, Entry>): string | null {
  const make = tiff.text(top.get(TAG_MAKE))?.trim() ?? '';
  const model = tiff.text(top.get(TAG_MODEL))?.trim() ?? '';
  const name = model.toLowerCase().startsWith(make.toLowerCase()) ? model : `${make} ${model}`.trim();
  return name ? name.slice(0, CAMERA_MAX) : null;
}

const EXIF_DATE = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
const UTC_OFFSET = /^[+-]\d{2}:\d{2}$/;

/** "2026:10:20 14:03:09" with "+10:30" to an ISO time, or null without a usable offset. */
export function exifTime(dateTime: string | null, offset: string | null): string | null {
  const parts = dateTime?.match(EXIF_DATE);
  if (!parts || !offset || !UTC_OFFSET.test(offset)) return null;
  const time = new Date(`${parts[1]}-${parts[2]}-${parts[3]}T${parts[4]}:${parts[5]}:${parts[6]}${offset}`);
  return Number.isNaN(time.getTime()) ? null : time.toISOString();
}

/** What a photo's EXIF says about where, which way and when it was taken. Unreadable = nothing. */
export function readPhotoExif(bytes: ArrayBuffer): PhotoExif {
  try {
    const view = new DataView(bytes);
    const start = findTiff(view);
    if (start === null) return NOTHING;
    const tiff = new Tiff(view, start);
    const topAt = tiff.firstIfd();
    const top = tiff.entries(topAt);
    const gpsAt = tiff.pointer(top.get(TAG_GPS_IFD));
    const exifAt = tiff.pointer(top.get(TAG_EXIF_IFD));
    // Each directory is read once, never followed from another, so no link can loop — but one
    // pointing back at another directory is malformed.
    const directories = [topAt, gpsAt, exifAt].filter((a) => a !== null);
    if (new Set(directories).size !== directories.length) throw new Malformed();
    const gps = gpsAt === null ? new Map<number, Entry>() : tiff.entries(gpsAt);
    const exif = exifAt === null ? new Map<number, Entry>() : tiff.entries(exifAt);
    return {
      position: readPosition(tiff, gps),
      bearing: readBearing(tiff, gps),
      takenAt: exifTime(tiff.text(exif.get(TAG_DATE_TIME_ORIGINAL)), tiff.text(exif.get(TAG_OFFSET_TIME_ORIGINAL))),
      camera: readCamera(tiff, top),
    };
  } catch {
    // Cut short or malformed: treat as having no EXIF.
    return NOTHING;
  }
}
